/**
 * Nostr Client for Doom Arena
 * Handles: matchmaking, game state, kill feeds, leaderboards, profiles
 * Uses NIP-01, NIP-59 (Gift Wraps), NIP-65 (Relay Lists)
 */

import { EventEmitter } from 'events';
import * as nip19 from 'nostr-tools/nip19';
import * as nip04 from 'nostr-tools/nip04';
import * as nip59 from 'nostr-tools/nip59';
import { finalizeEvent, generateSecretKey, getPublicKey, verifyEvent } from 'nostr-tools/pure';
import type { UnsignedEvent } from 'nostr-tools/pure';
import { SimplePool } from 'nostr-tools/pool';
import type { Filter } from 'nostr-tools/filter';
import { hexToBytes, bytesToHex } from '@noble/hashes/utils';
import { sha256 } from '@noble/hashes/sha256';

// ============================================================================
// TYPES
// ============================================================================

export interface NostrConfig {
  relays: string[];
  autoConnect: boolean;
  reconnectInterval: number;
  maxReconnectAttempts: number;
  secretKey?: string; // hex
}

export interface NostrEvent {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
  tags: string[][];
  content: string;
  sig: string;
}

export interface PlayerProfile {
  name: string;
  about: string;
  picture: string;
  lud16: string; // Lightning address
  elo: number;
  wins: number;
  losses: number;
  kills: number;
  deaths: number;
  favoriteCharacter: string;
  cosmetics: string[];
}

export interface MatchmakingRequest {
  skill: number;
  character: string;
  sessionToken: string;
  mode: 'duel' | 'ffa' | 'tournament';
  region?: string;
}

export interface LobbyState {
  matchId: string;
  players: LobbyPlayer[];
  map: string;
  mode: string;
  status: 'waiting' | 'starting' | 'in_progress' | 'finished';
  createdAt: number;
  startedAt?: number;
}

export interface LobbyPlayer {
  pubkey: string;
  name: string;
  character: string;
  elo: number;
  ready: boolean;
  team?: number;
}

export interface GameStateSnapshot {
  matchId: string;
  player: string;
  character: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number; w: number };
  health: number;
  ammo: number;
  timestamp: number;
}

export interface KillEvent {
  matchId: string;
  killer: string;
  victim: string;
  weapon: string;
  distance: number;
  headshot: boolean;
  timestamp: number;
  bounty: number;
}

export interface MatchResult {
  matchId: string;
  mode: string;
  map: string;
  players: MatchPlayerResult[];
  duration: number;
  timestamp: number;
}

export interface MatchPlayerResult {
  pubkey: string;
  name: string;
  character: string;
  kills: number;
  deaths: number;
  damage: number;
  score: number;
  eloChange: number;
  bountyEarned: number;
  placement: number;
}

export type NetworkStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

// ============================================================================
// EVENTS
// ============================================================================

export type NostrEvents = {
  connected: () => void;
  disconnected: () => void;
  error: (error: Error) => void;
  statusChange: (status: NetworkStatus) => void;
  matchFound: (lobby: LobbyState) => void;
  matchUpdate: (lobby: LobbyState) => void;
  matchStarted: (lobby: LobbyState) => void;
  gameStateUpdate: (state: GameStateSnapshot) => void;
  killFeed: (kill: KillEvent) => void;
  matchEnded: (result: MatchResult) => void;
  profileUpdate: (pubkey: string, profile: PlayerProfile) => void;
  leaderboardUpdate: (leaderboard: LeaderboardEntry[]) => void;
};

export interface LeaderboardEntry {
  pubkey: string;
  name: string;
  elo: number;
  wins: number;
  losses: number;
  kills: number;
  deaths: number;
  totalBounty: number;
  rank: number;
}

// ============================================================================
// Nostr Client
// ============================================================================

export class NostrClient extends EventEmitter {
  public config: NostrConfig;
  public pool: SimplePool;
  private secretKey: Uint8Array | null = null;
  public publicKey: string = '';
  private status: NetworkStatus = 'disconnected';
  private connectedRelays: Set<string> = new Set();
  private reconnectAttempts = 0;
  private subscriptions: Map<string, { close: (reason?: string) => void }> = new Map();
  private subscribed = false;
  private pendingRequests: Map<string, { resolve: (v: any) => void; reject: (e: Error) => void; timeout: ReturnType<typeof setTimeout> }> = new Map();
  private requestId = 0;
  private profileCache: Map<string, PlayerProfile> = new Map();
  private lobbyCache: Map<string, LobbyState> = new Map();

  constructor(config: Partial<NostrConfig> = {}) {
    super();
    // Prevent uncaught 'error' events when no listener is registered yet
    this.on('error', () => {});
    this.config = {
      relays: config.relays || ['wss://relay.21pay.org', 'wss://relay.damus.io', 'wss://nos.lol'],
      autoConnect: config.autoConnect ?? true,
      reconnectInterval: config.reconnectInterval || 3000,
      maxReconnectAttempts: config.maxReconnectAttempts || 10,
    };

    this.pool = new SimplePool();

    // Generate or load secret key
    if (config.secretKey) {
      const sk = hexToBytes(config.secretKey);
      this.secretKey = sk;
      this.publicKey = getPublicKey(sk);
    } else {
      this.generateKeys();
    }
  }

  private generateKeys(): void {
    const sk = generateSecretKey();
    this.secretKey = sk;
    this.publicKey = getPublicKey(sk);
    console.log('[Nostr] Generated keys:', this.publicKey);
  }

  // ============================================================================
  // CONNECTION
  // ============================================================================

  async connect(): Promise<void> {
    if (this.status === 'connected' || this.status === 'connecting') return;

    this.setStatus('connecting');

    try {
      // Test relay connections
      const results = await Promise.allSettled(
        this.config.relays.map(url => this.testRelay(url))
      );

      const workingRelays = this.config.relays.filter((_, i) => results[i].status === 'fulfilled');
      
      if (workingRelays.length === 0) {
        throw new Error('No working relays found');
      }

      console.log('[Nostr] Connected to relays:', workingRelays);
      this.connectedRelays = new Set(workingRelays);
      this.reconnectAttempts = 0;
      this.setStatus('connected');
      this.emit('connected');

      // NOTE: on ne s'abonne PLUS ici. Les filtres { kinds: [30000] } et
      // { kinds: [30005] } ne sont bornés ni par auteur ni par match : sur des
      // relais publics c'est un firehose, et chaque événement reçu passe par
      // verifyEvent() (Schnorr, synchrone) sur le thread principal → saccades
      // en plein match. L'abonnement est devenu paresseux : seul le flux qui
      // consomme réellement les événements entrants l'ouvre (ensureSubscribed,
      // appelé par joinMatchmaking). Publier (kills, profils) n'en a pas besoin.

      // Publish relay list (NIP-65)
      await this.publishRelayList();

    } catch (error) {
      this.setStatus('error');
      this.emit('error', error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    }
  }

  private async testRelay(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      const timeout = setTimeout(() => {
        ws.close();
        reject(new Error('Connection timeout'));
      }, 5000);

      ws.onopen = () => {
        clearTimeout(timeout);
        ws.close();
        resolve();
      };
      ws.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Connection failed'));
      };
    });
  }

  disconnect(): void {
    // Close all subscriptions
    for (const [, sub] of this.subscriptions) {
      try { sub.close(); } catch {}
    }
    this.subscriptions.clear();
    this.subscribed = false;

    this.pool.close(this.config.relays);
    this.connectedRelays.clear();
    this.setStatus('disconnected');
    this.emit('disconnected');
  }

  private setStatus(status: NetworkStatus): void {
    this.status = status;
    this.emit('statusChange', status);
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.emit('error', new Error('Max reconnect attempts reached'));
      return;
    }

    const delay = this.config.reconnectInterval * Math.pow(1.5, this.reconnectAttempts);
    this.reconnectAttempts++;

    setTimeout(() => {
      this.connect().catch(() => {});
    }, delay);
  }

  // ============================================================================
  // EVENT SUBSCRIPTIONS
  // ============================================================================

  /**
   * Ouvre les abonnements une seule fois, à la demande. Appelé par les flux qui
   * ont besoin des événements entrants (matchmaking / lobby) — jamais depuis
   * connect(), pour ne pas faire tourner un firehose vérifié en Schnorr pendant
   * une partie qui ne fait que publier.
   */
  private async ensureSubscribed(): Promise<void> {
    if (this.subscribed) return;
    this.subscribed = true;
    await this.subscribeToEvents();
  }

  private async subscribeToEvents(): Promise<void> {
    const filters: Filter[] = [
      // Direct messages to us (Gift Wraps - NIP-59)
      { kinds: [1059], '#p': [this.publicKey] },
      // Lobby state updates
      { kinds: [30003], '#p': [this.publicKey] },
      // Game state updates for matches we're in
      { kinds: [30000] },
      // Kill feed
      { kinds: [30005] },
      // Match results
      { kinds: [30001] },
      // Leaderboard snapshots
      { kinds: [30008] },
    ];

    for (const filter of filters) {
      const subId = `sub_${++this.requestId}`;
      const closer = this.pool.subscribe(this.config.relays, filter, {
        onevent: (event) => this.handleEvent(event),
        oneose: () => console.log('[Nostr] EOSE for', filter.kinds),
      });
      this.subscriptions.set(subId, closer);
    }
  }

  private handleEvent(event: NostrEvent): void {
    try {
      // Verify event
      if (!verifyEvent(event)) {
        console.warn('[Nostr] Invalid event signature');
        return;
      }

      switch (event.kind) {
        case 1059: // Gift Wrap - unwrap and handle
          this.handleGiftWrap(event);
          break;
        case 30000: // Game State
          this.handleGameState(event);
          break;
        case 30001: // Match Result
          this.handleMatchResult(event);
          break;
        case 30003: // Lobby State
          this.handleLobbyState(event);
          break;
        case 30005: // Kill Feed
          this.handleKillFeed(event);
          break;
        case 30008: // Leaderboard
          this.handleLeaderboard(event);
          break;
        case 0: // Metadata
          this.handleMetadata(event);
          break;
      }
    } catch (error) {
      console.error('[Nostr] Event handling error:', error);
    }
  }

  private async handleGiftWrap(event: NostrEvent): Promise<void> {
    if (!this.secretKey) return;
    
    try {
      const rumor = nip59.unwrapEvent(event, this.secretKey);
      // Handle unwrapped event based on its kind
      this.handleEvent(rumor as unknown as NostrEvent);
    } catch (error) {
      console.warn('[Nostr] Failed to unwrap gift wrap:', error);
    }
  }

  private handleGameState(event: NostrEvent): void {
    try {
      const state = JSON.parse(event.content) as GameStateSnapshot;
      this.emit('gameStateUpdate', state);
    } catch {}
  }

  private handleMatchResult(event: NostrEvent): void {
    try {
      const result = JSON.parse(event.content) as MatchResult;
      this.emit('matchEnded', result);
    } catch {}
  }

  private handleLobbyState(event: NostrEvent): void {
    try {
      const lobby = JSON.parse(event.content) as LobbyState;
      this.lobbyCache.set(lobby.matchId, lobby);
      
      switch (lobby.status) {
        case 'waiting':
          this.emit('matchFound', lobby);
          break;
        case 'starting':
        case 'in_progress':
          this.emit('matchUpdate', lobby);
          break;
      }
    } catch {}
  }

  private handleKillFeed(event: NostrEvent): void {
    try {
      const kill = JSON.parse(event.content) as KillEvent;
      this.emit('killFeed', kill);
    } catch {}
  }

  private handleLeaderboard(event: NostrEvent): void {
    try {
      const leaderboard = JSON.parse(event.content) as LeaderboardEntry[];
      this.emit('leaderboardUpdate', leaderboard);
    } catch {}
  }

  private handleMetadata(event: NostrEvent): void {
    try {
      const profile = JSON.parse(event.content) as PlayerProfile;
      this.profileCache.set(event.pubkey, profile);
      this.emit('profileUpdate', event.pubkey, profile);
    } catch {}
  }

  // ============================================================================
  // PUBLISHING
  // ============================================================================

  private async publishEvent(event: NostrEvent): Promise<string> {
    const results = await Promise.allSettled(this.pool.publish(this.config.relays, event));
    const success = results.some(r => r.status === 'fulfilled');
    if (!success) throw new Error('Failed to publish to any relay');
    return event.id;
  }

  private createEvent(kind: number, content: string, tags: string[][] = []): NostrEvent {
    if (!this.secretKey) throw new Error('No secret key');
    const sk = this.secretKey;

    const unsigned = {
      kind,
      content,
      tags,
      created_at: Math.floor(Date.now() / 1000),
      pubkey: this.publicKey,
    };

    return finalizeEvent(unsigned, sk);
  }

  private async sendGiftWrap(recipientPubkey: string, kind: number, content: string, tags: string[][] = []): Promise<string> {
    if (!this.secretKey) throw new Error('No secret key');

    const rumor: Partial<UnsignedEvent> = {
      kind,
      content,
      tags,
      created_at: Math.floor(Date.now() / 1000),
    };
    const wrap = nip59.wrapEvent(rumor, this.secretKey, recipientPubkey);
    return this.publishEvent(wrap);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  async publishProfile(profile: PlayerProfile): Promise<string> {
    const event = this.createEvent(
      0,
      JSON.stringify(profile),
      [
        ['d', 'profile'],
        ['name', profile.name],
      ]
    );
    return this.publishEvent(event);
  }

  async getProfile(pubkey: string): Promise<PlayerProfile | null> {
    // Check cache first
    if (this.profileCache.has(pubkey)) {
      return this.profileCache.get(pubkey)!;
    }

    // Fetch from relays
    const events = await this.pool.querySync(this.config.relays, {
      kinds: [0],
      authors: [pubkey],
      limit: 1,
    });

    if (events.length > 0) {
      try {
        const profile = JSON.parse(events[0].content) as PlayerProfile;
        this.profileCache.set(pubkey, profile);
        return profile;
      } catch {}
    }

    return null;
  }

  async joinMatchmaking(request: MatchmakingRequest): Promise<string> {
    // seul point du client qui dépend des événements entrants → c'est lui qui
    // ouvre les abonnements (cf. ensureSubscribed)
    await this.ensureSubscribed();
    const matchId = `match_${Date.now()}_${bytesToHex(sha256(new TextEncoder().encode(this.publicKey))).slice(0, 8)}`;
    
    // Publish matchmaking request
    const event = this.createEvent(
      30004,
      JSON.stringify(request),
      [
        ['d', matchId],
        ['mode', request.mode],
        ['character', request.character],
        ['skill', request.skill.toString()],
        ['session', request.sessionToken],
      ]
    );
    
    await this.publishEvent(event);
    
    // Wait for match found (with timeout)
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.off('matchFound', onMatchFound);
        reject(new Error('Matchmaking timeout'));
      }, 60000);

      const onMatchFound = (lobby: LobbyState) => {
        if (lobby.players.some(p => p.pubkey === this.publicKey)) {
          clearTimeout(timeout);
          this.off('matchFound', onMatchFound);
          resolve(lobby.matchId);
        }
      };

      this.on('matchFound', onMatchFound);
    });
  }

  async publishGameState(state: GameStateSnapshot): Promise<void> {
    const event = this.createEvent(
      30000,
      JSON.stringify(state),
      [
        ['d', `state_${state.matchId}_${state.player}`],
        ['match', state.matchId],
        ['player', state.player],
      ]
    );
    
    await this.publishEvent(event);
  }

  async publishShot(matchId: string, origin: { x: number; y: number; z: number }, direction: { x: number; y: number; z: number }): Promise<void> {
    const event = this.createEvent(
      30005,
      JSON.stringify({
        matchId,
        shooter: this.publicKey,
        origin,
        direction,
        timestamp: Date.now(),
      } as any),
      [
        ['d', `shot_${matchId}_${Date.now()}`],
        ['match', matchId],
        ['type', 'shot'],
      ]
    );
    
    await this.publishEvent(event);
  }

  async publishKill(kill: KillEvent): Promise<void> {
    const event = this.createEvent(
      30005,
      JSON.stringify(kill),
      [
        ['d', `kill_${kill.matchId}_${Date.now()}`],
        ['match', kill.matchId],
        ['killer', kill.killer],
        ['victim', kill.victim],
        ['type', 'kill'],
      ]
    );
    
    await this.publishEvent(event);
  }

  async publishMatchResult(result: MatchResult): Promise<void> {
    const event = this.createEvent(
      30001,
      JSON.stringify(result),
      [
        ['d', `result_${result.matchId}`],
        ['match', result.matchId],
        ['mode', result.mode],
        ['map', result.map],
      ]
    );
    
    await this.publishEvent(event);
  }

  async publishLobbyState(lobby: LobbyState): Promise<void> {
    const event = this.createEvent(
      30003,
      JSON.stringify(lobby),
      [
        ['d', `lobby_${lobby.matchId}`],
        ['match', lobby.matchId],
        ['status', lobby.status],
      ]
    );
    
    await this.publishEvent(event);
  }

  async publishRelayList(): Promise<void> {
    const relayTags = this.config.relays.map(url => ['r', url, 'read+write']);
    
    const event = this.createEvent(
      10002,
      '',
      relayTags
    );
    
    await this.publishEvent(event);
  }

  async sendDirectMessage(recipientPubkey: string, content: string): Promise<string> {
    return this.sendGiftWrap(recipientPubkey, 4, content, [['p', recipientPubkey]]);
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  getPublicKey(): string {
    return this.publicKey;
  }

  getPublicKeyNpub(): string {
    return nip19.npubEncode(this.publicKey);
  }

  getSecretKeyHex(): string {
    return this.secretKey ? bytesToHex(this.secretKey) : '';
  }

  getStatus(): NetworkStatus {
    return this.status;
  }

  getConnectedRelays(): string[] {
    return Array.from(this.connectedRelays);
  }

  async encryptMessage(recipientPubkey: string, content: string): Promise<string> {
    if (!this.secretKey) throw new Error('No secret key');
    return nip04.encrypt(this.secretKey, recipientPubkey, content);
  }

  async decryptMessage(senderPubkey: string, content: string): Promise<string> {
    if (!this.secretKey) throw new Error('No secret key');
    return nip04.decrypt(this.secretKey, senderPubkey, content);
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

let nostrClientInstance: NostrClient | null = null;

export function getNostrClient(config?: Partial<NostrConfig>): NostrClient {
  if (!nostrClientInstance) {
    nostrClientInstance = new NostrClient(config);
  }
  return nostrClientInstance;
}

export async function initializeNostr(config?: Partial<NostrConfig>): Promise<NostrClient> {
  const client = getNostrClient(config);
  await client.connect();
  return client;
}